import { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export interface Comment {
  _id?: string;
  plantId: string;
  name: string;
  ailment: string;
  experience: string;
  preparationMethod: string;
  dosage: string;
  sideEffects: string;
  createdAt?: string;
}

interface PlantCommentsProps {
  plantId: string;
  onCommentAdded?: () => Promise<void>;
}

const PlantComments = ({ plantId, onCommentAdded }: PlantCommentsProps) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Omit<Comment, 'plantId' | 'createdAt'>>({ 
    name: '',
    ailment: '',
    experience: '',
    preparationMethod: '',
    dosage: '',
    sideEffects: ''
  });


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== Form submission started ===');
    setError('');
    setSuccess('');
    
    // Prevent multiple submissions
    if (isSubmitting) {
      console.log('Preventing duplicate submission');
      return;
    }
    
    console.log('Setting isSubmitting to true');
    setIsSubmitting(true);
    
    // Create a controller for the fetch request (for better timeout handling)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const data = { ...formData, plantId };
      console.log('Form data prepared:', data);
      
      // Make the API call using fetch for better timeout control
      console.log('Making API call to:', `${API_BASE_URL}/api/plants/${plantId}/comments`);
      
      const response = await fetch(`${API_BASE_URL}/api/plants/${plantId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        signal: controller.signal
      });
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      const responseData = await response.json().catch(() => ({}));
      
      console.log('API response received:', {
        status: response.status,
        statusText: response.statusText,
        data: responseData
      });
      
      if (!response.ok) {
        throw new Error(responseData?.message || `Server responded with status ${response.status}`);
      }
      
      // Reset form
      console.log('Resetting form data');
      setFormData({
        name: '',
        ailment: '',
        experience: '',
        preparationMethod: '',
        dosage: '',
        sideEffects: ''
      });
      
      console.log('Setting success message');
      setSuccess('Comment submitted successfully!');
      
      // Call the onCommentAdded callback if provided
      if (onCommentAdded) {
        console.log('Calling onCommentAdded callback');
        try {
          await onCommentAdded();
          console.log('onCommentAdded callback completed successfully');
        } catch (callbackError) {
          console.error('Error in onCommentAdded callback:', callbackError);
          // Don't show this error to the user, just log it
        }
      } else {
        console.log('No onCommentAdded callback provided');
      }
      
      // Clear success message after 3 seconds
      const timer = setTimeout(() => {
        console.log('Clearing success message');
        setSuccess('');
      }, 3000);
      
      // Clean up the timer if the component unmounts
      return () => {
        console.log('Cleaning up timer');
        clearTimeout(timer);
      };
      
    } catch (error: any) {
      console.error('Error during form submission:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to submit comment. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'The request took too long. The server might be busy. Please try again in a moment.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
      
      // If there's a specific field error, log it
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
    } finally {
      console.log('Setting isSubmitting to false');
      setIsSubmitting(false);
      console.log('=== Form submission completed ===');
    }
  };


  return (
    <div className="mt-12">
      <div className="grid grid-cols-1">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Share Your Experience</h2>
          
          {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="How you'd like to be identified (can be a nickname)"
                required
              />
            </div>
            
            <div>
              <label htmlFor="ailment" className="block text-sm font-medium text-gray-700 mb-1">
                Ailment or Condition <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="ailment"
                name="ailment"
                value={formData.ailment}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="What health issue were you addressing?"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
              Your Experience <span className="text-red-500">*</span>
            </label>
            <textarea
              id="experience"
              name="experience"
              rows={4}
              value={formData.experience}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Describe your experience with this plant, including how you used it and what effects you noticed"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="preparationMethod" className="block text-sm font-medium text-gray-700 mb-1">
                Preparation Method <span className="text-red-500">*</span>
              </label>
              <select
                id="preparationMethod"
                name="preparationMethod"
                value={formData.preparationMethod}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select a preparation method</option>
                <option value="Tea/Infusion">Tea/Infusion - Steeping plant material in hot water</option>
                <option value="Tincture">Tincture - Alcohol or glycerin extract</option>
                <option value="Capsule">Capsule - Dried herb in pill form</option>
                <option value="Poultice">Poultice - Crushed fresh plant applied to skin</option>
                <option value="Essential Oil">Essential Oil - Concentrated plant extract</option>
                <option value="Decoction">Decoction - Simmering tough plant parts</option>
                <option value="Syrup">Syrup - Herbal extract in sweet base</option>
                <option value="Other">Other (please specify in experience)</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="dosage" className="block text-sm font-medium text-gray-700 mb-1">
                Dosage
              </label>
              <input
                type="text"
                id="dosage"
                name="dosage"
                value={formData.dosage}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="How much and how often did you take/use it?"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="sideEffects" className="block text-sm font-medium text-gray-700 mb-1">
              Side Effects (if any)
            </label>
            <textarea
              id="sideEffects"
              name="sideEffects"
              rows={2}
              value={formData.sideEffects}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Any negative reactions or things others should be aware of?"
            />
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                isSubmitting 
                  ? 'bg-green-400' 
                  : 'bg-green-600 hover:bg-green-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : 'Submit Experience'}
            </button>
          </div>
          
          <div className="text-xs text-gray-500">
            <p className="mb-2"><span className="text-red-500">*</span> Required fields</p>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlantComments;